import { z } from 'zod';

/* Contratos de verificação de publishers no backoffice (ADR-0405). O admin-service
   expõe `GET /v1/admin/publishers` (list) + `POST /v1/admin/publishers/:id/verify`
   (status→verified + root-sign das chaves), lendo/escrevendo no licensing via HMAC.
   Fecha o ciclo de publicação community: sem root-sign, os packs scoped do publisher
   falham o verify-with-chain do registry (ADR-0404). Shape espelha o licensing. */

/* `suspended` = reversível (ADR-0471); `banned` = terminal reversível só por super_admin.
   Ambos derrubam o fastpath (exige `verified`). Coluna text no licensing (sem migração). */
export const publisherStatusSchema = z.enum(['pending', 'verified', 'suspended', 'banned']);
export type PublisherStatus = z.infer<typeof publisherStatusSchema>;

export const publisherKeyDto = z.object({
  id: z.string(),
  fingerprint: z.string(),
  rootSigned: z.boolean(),
  revoked: z.boolean(),
});
export type PublisherKey = z.infer<typeof publisherKeyDto>;

export const publisherDto = z.object({
  id: z.string(),
  slug: z.string(),
  displayName: z.string(),
  status: publisherStatusSchema,
  createdAt: z.string(),
  verifiedAt: z.string().nullish(),
  keys: z.array(publisherKeyDto),
});
export type Publisher = z.infer<typeof publisherDto>;

export const publishersListResponseDto = z.object({
  publishers: z.array(publisherDto),
});
export type PublishersListResponse = z.infer<typeof publishersListResponseDto>;

export const verifyPublisherResponseDto = z.object({
  publisher: z.object({
    id: z.string(),
    slug: z.string(),
    status: publisherStatusSchema,
  }),
  signedKeyIds: z.array(z.string()),
});
export type VerifyPublisherResponse = z.infer<typeof verifyPublisherResponseDto>;

/* Governança de publisher no backoffice (TASK-PUB-20, ADR-0471). */
export const setPublisherStatusBody = z.object({
  status: publisherStatusSchema,
  reason: z.string().min(1),
});
export type SetPublisherStatusBody = z.infer<typeof setPublisherStatusBody>;

/* Provisionamento de publisher pelo backoffice (ADR-0483, espelha org admin-created ADR-0421):
   staff designa um `ownerUserId` (conta existente) como dono do publisher. Diferente do onboard
   self-service, este caminho PERMITE slugs reservados (`planuze` etc.) — o dono legítimo reivindica
   seu namespace sem enfraquecer o anti-squatting (ADR-0454), que segue bloqueando terceiros. */
export const provisionPublisherBody = z.object({
  slug: z.string().min(1).max(64),
  displayName: z.string().min(1).max(80),
  ownerUserId: z.string().min(1),
  websiteUrl: z.string().url().optional(),
});
export type ProvisionPublisherBody = z.infer<typeof provisionPublisherBody>;

export const provisionPublisherResponseDto = z.object({
  publisher: z.object({
    id: z.string(),
    slug: z.string(),
    displayName: z.string(),
    status: publisherStatusSchema,
  }),
});
export type ProvisionPublisherResponse = z.infer<typeof provisionPublisherResponseDto>;

/* Gestão de slugs reservados no admin (ADR-0484). Mesmo formato de slug do onboard
   (`publisher.slugPattern`): minúsculas, dígitos e hífen, 1–39 chars, sem hífen nas pontas. */
export const RESERVED_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,37}[a-z0-9])?$/;
const MAX_RESERVED_SLUGS = 200;

export const reservedSlugsResponseDto = z.object({ slugs: z.array(z.string()) });
export type ReservedSlugsResponse = z.infer<typeof reservedSlugsResponseDto>;

/* Substitui a lista inteira (RMW no config-service). Cada slug validado + o service normaliza
   (lowercase) e deduplica antes de persistir. */
export const updateReservedSlugsBody = z.object({
  slugs: z.array(z.string().min(1).max(64).regex(RESERVED_SLUG_RE)).max(MAX_RESERVED_SLUGS),
});
export type UpdateReservedSlugsBody = z.infer<typeof updateReservedSlugsBody>;

/* Stats derivados da fila de review (GROUP BY publisherId). `avgReviewMs` null sem terminais. */
export const publisherStatsDto = z.object({
  totalSubmissions: z.number().int().nonnegative(),
  pendingSubmissions: z.number().int().nonnegative(),
  rejectionRate: z.number().min(0).max(1),
  avgReviewMs: z.number().int().nonnegative().nullable(),
});
export type PublisherStats = z.infer<typeof publisherStatsDto>;

export const publisherWithStatsDto = publisherDto.extend({
  stats: publisherStatsDto.nullish(),
});
export type PublisherWithStats = z.infer<typeof publisherWithStatsDto>;

export const publishersWithStatsListResponseDto = z.object({
  publishers: z.array(publisherWithStatsDto),
});
export type PublishersWithStatsListResponse = z.infer<typeof publishersWithStatsListResponseDto>;

/* Detalhe de um publisher (TASK-PUB-27, ADR-0476): identidade + chaves + stats + `domainVerified`
   (read-only; insumo/selo, NÃO gate do fastpath — ADR-0374/0423). Estende o shape do list com o
   `domainVerified` que o `GET /v1/internal/publishers/:id` do licensing passou a expor. */
export const publisherDetailDto = publisherWithStatsDto.extend({
  domainVerified: z.boolean(),
});
export type PublisherDetail = z.infer<typeof publisherDetailDto>;

export const publisherDetailResponseDto = z.object({ publisher: publisherDetailDto });
export type PublisherDetailResponse = z.infer<typeof publisherDetailResponseDto>;

/* Ações de governança (verificar/suspender/banir + reversos). */
export const publisherActionSchema = z.enum(['verify', 'suspend', 'unsuspend', 'ban', 'unban']);
export type PublisherAction = z.infer<typeof publisherActionSchema>;

/* Ação de governança (exceto `verify`, que root-assina em trilha própria) → status alvo. Fonte
   única do admin-service (bulk) e do admin-web (UI otimista): divergir mostraria status errado. */
export const publisherActionToStatus: Readonly<
  Record<Exclude<PublisherAction, 'verify'>, PublisherStatus>
> = {
  suspend: 'suspended',
  unsuspend: 'pending',
  ban: 'banned',
  unban: 'pending',
};

/** Ações chamam o Licensing sequencialmente; o teto evita requests privilegiadas sem limite. */
export const PUBLISHER_BULK_MAX_IDS = 100;
export const bulkPublisherActionBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(PUBLISHER_BULK_MAX_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  action: publisherActionSchema,
  reason: z.string().min(1),
});
export type BulkPublisherActionBody = z.infer<typeof bulkPublisherActionBody>;

export const bulkPublisherActionResponseDto = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().nullish() })),
});
export type BulkPublisherActionResponse = z.infer<typeof bulkPublisherActionResponseDto>;

/* Trilha de atividade de um publisher (aba Atividade do backoffice — resolve "publisher sem
   histórico"). Espelha a página keyset que o licensing expõe em
   `/v1/internal/publishers/:id/activity` (`{ items, nextCursor }`, mais recente primeiro,
   ADR-0466). `action`/`targetType` são strings abertas de propósito — o licensing evolui o
   catálogo de eventos sem exigir migração de contrato; a UI faz fallback ao valor cru.
   `metadata` é opaco (detalhe por evento, ex.: label do token). */
export const publisherActivityEntryDto = z.object({
  id: z.string(),
  /** Membro que realizou a ação; `null` quando o ator não é resolvível. */
  actorUserId: z.string().nullable(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  metadata: z.unknown(),
  ipAddress: z.string().nullable(),
  ts: z.string(),
});
export type PublisherActivityEntry = z.infer<typeof publisherActivityEntryDto>;

export const publisherActivityPageDto = z.object({
  items: z.array(publisherActivityEntryDto),
  /** Cursor opaco `${tsEpochMs}_${id}` da próxima página; `null` quando é a última. */
  nextCursor: z.string().nullable(),
});
export type PublisherActivityPage = z.infer<typeof publisherActivityPageDto>;
